import { z } from 'zod';
import { RecurringInvoicePeriodicitySchema, RecurringStartAnchorSchema } from './recurring';
export { RecurringStartAnchorSchema } from './recurring';
export const PipelineStageSchema = z.enum([
    'CANCELADA',
    'LEAD',
    'PROSPECTO',
    'OPORTUNIDAD',
    'PROPUESTA_EN_PREPARACION',
    'PROPUESTA_ENVIADA',
    'EN_INVERSION',
    'GANADA',
    'CONGELADA',
    'PERDIDA',
    'EN_ESPERA',
]);
export const PipelineStageLabel = {
    // Cancelada: salió del pipeline sin competirse. No cuenta como pérdida.
    CANCELADA: 'Cancelada',
    LEAD: 'Lead',
    PROSPECTO: 'Prospecto',
    OPORTUNIDAD: 'Oportunidad',
    PROPUESTA_EN_PREPARACION: 'Propuesta en preparación',
    PROPUESTA_ENVIADA: 'Propuesta enviada',
    // En Inversión: como GANADA, genera proyecto (IA4Equity / apuestas propias).
    EN_INVERSION: 'En Inversión',
    GANADA: 'Ganada',
    // Congelada: aparcada sin fecha pero recuperable. Pondera 0 en la previsión;
    // «En espera» es la pausa corta que sigue contando al 30 %.
    CONGELADA: 'Congelada',
    PERDIDA: 'Perdida',
    EN_ESPERA: 'En espera',
};
/**
 * M11 — Las líneas de servicio viven en el catálogo service_line (BD), no en
 * un enum. El schema valida el FORMATO del código; la existencia/actividad se
 * valida en el backend contra el catálogo.
 */
export const ServiceLineSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Z0-9_]+$/, 'Código de línea de servicio no válido');
/**
 * @deprecated M11 — etiquetas legadas de las 3 líneas históricas. Úsalo solo
 * como fallback: los nombres reales vienen del catálogo (GET /service-lines).
 */
export const ServiceLineLabel = {
    CONSULTORIA_IA: 'Consultoría IA',
    DESARROLLO_AGENTICO: 'Desarrollo agéntico',
    AUTOMATIZACION: 'Automatización',
};
/**
 * Mapa de valor por línea de servicio (en céntimos).
 * Ej.: { CONSULTORIA_IA: 30000, AUTOMATIZACION: 20000 }.
 * Solo se incluyen las líneas marcadas; el valor puede ser 0 si la línea está
 * marcada sin importe asignado. Las claves se validan contra el catálogo en backend.
 */
export const ServiceLineValuesSchema = z.record(ServiceLineSchema, z.number().int().min(0));
/** M12 — Modo de facturación: PROBONO/PRO_EQUITY se trazan fuera del pipeline facturable. */
export const OpportunityBillingModeSchema = z.enum(['FACTURABLE', 'PROBONO', 'PRO_EQUITY']);
/**
 * LOTE B — Detalle de una línea de servicio DENTRO de una oportunidad. La
 * recurrencia y el modo de facturación son específicos de la oportunidad (no
 * del catálogo): el mismo servicio puede ser mensual en un proyecto y anual en
 * otro, o facturable en uno y probono en otro.
 */
export const OpportunityServiceLineSchema = z.object({
    /** Identidad estable de la línea (id de la fila OpportunityServiceLine). El
     *  editor la usa como key para poder repetir el mismo `code` en varias líneas. */
    id: z.string(),
    code: ServiceLineSchema,
    valueCents: z.number().int().min(0),
    billingMode: OpportunityBillingModeSchema,
    isRecurring: z.boolean(),
    periodicity: RecurringInvoicePeriodicitySchema.nullable(),
    dayOfMonth: z.number().int().nullable(),
    /**
     * Mantenimiento con arranque diferido: fecha de inicio de facturación de esta
     * línea recurrente (null = arranca en la próxima ocurrencia desde hoy). El
     * wizard al GANAR crea la iguala con esta fecha; el motor no factura antes.
     */
    recurringStartDate: z.string().datetime().nullable(),
    /** Ancla de inicio: 'DATE' (usa recurringStartDate) o una fase de ejecución. */
    recurringStartAnchor: RecurringStartAnchorSchema.nullable(),
    occurrences: z.number().int().nullable(),
    endsAt: z.string().datetime().nullable(),
});
/** Entrada por línea para crear/editar una oportunidad (POST/PATCH). */
export const OpportunityServiceLineInputSchema = z.object({
    code: ServiceLineSchema,
    valueCents: z.number().int().min(0).default(0),
    billingMode: OpportunityBillingModeSchema.default('FACTURABLE'),
    isRecurring: z.boolean().default(false),
    periodicity: RecurringInvoicePeriodicitySchema.nullable().optional(),
    dayOfMonth: z.number().int().min(1).max(28).nullable().optional(),
    recurringStartDate: z.string().datetime().nullable().optional(),
    recurringStartAnchor: RecurringStartAnchorSchema.nullable().optional(),
    occurrences: z.number().int().positive().nullable().optional(),
    endsAt: z.string().datetime().nullable().optional(),
});
// Pieza 5 — Ciclo de ejecución (proyecto ganado), distinto del pipeline comercial.
export const ExecutionPhaseSchema = z.enum([
    'ONBOARDING',
    'EJECUCION',
    'FINALIZADO',
    'SOPORTE',
]);
export const ExecutionPhaseLabel = {
    ONBOARDING: 'Onboarding',
    EJECUCION: 'Ejecución',
    FINALIZADO: 'Finalizado',
    SOPORTE: 'Soporte',
};
/** Orden de las fases para el stepper de ejecución. */
export const ExecutionPhaseOrder = [
    'ONBOARDING',
    'EJECUCION',
    'FINALIZADO',
    'SOPORTE',
];
export const RoadmapItemStatusSchema = z.enum([
    'PENDIENTE',
    'EN_CURSO',
    'COMPLETADO',
    'BLOQUEADO',
]);
export const RoadmapItemStatusLabel = {
    PENDIENTE: 'Pendiente',
    EN_CURSO: 'En curso',
    COMPLETADO: 'Completado',
    BLOQUEADO: 'Bloqueado',
};
// F33: AssignmentRole eliminado (era enum LEAD/COLABORADOR/ANALISTA).
// El rol funcional vive ahora en ProjectRole (catálogo dinámico, CRUD desde Ajustes).
// Cada asignación lleva projectRoleId + hoursAllocated + snapshots de tarifa/coste.
const optionalString = (max = 200) => z.string().max(max).nullable().optional();
export const CreateOpportunitySchema = z.object({
    title: z.string().min(2, 'Título demasiado corto').max(300),
    clientId: z.string().min(1, 'Selecciona un cliente'),
    /**
     * Línea de servicio principal. Opcional: si se omite y serviceLineValuesCents
     * tiene keys, el backend la deriva (la de mayor valor; o la primera marcada si
     * todas son 0). Si se pasa explícitamente, se respeta.
     */
    serviceLine: ServiceLineSchema.optional(),
    stage: PipelineStageSchema.default('LEAD'),
    /** M12 — Probono/Pro-Equity: valor trazable fuera del pipeline facturable. */
    billingMode: OpportunityBillingModeSchema.default('FACTURABLE'),
    description: optionalString(5000),
    lostReason: optionalString(2000),
    /**
     * Cierre ADMINISTRATIVO: se marca PERDIDA solo para sacarla del pipeline
     * (duplicada, refacturada a otro cliente, alta errónea…), pero no es
     * negocio perdido y no cuenta en el KPI de perdidas ni en la conversión.
     */
    administrativeClose: z.boolean().optional(),
    openedAt: z.string().datetime().optional(),
    advisorId: z.string().nullable().optional(),
    /** Comisión del asesor como fracción (0.05 = 5%). Tope de negocio: 0.30 (30%). */
    advisorCommissionPct: z.number().min(0).max(0.3).nullable().optional(),
    /**
     * Valor estimado manual del proyecto (en céntimos). Si serviceLineValuesCents
     * tiene keys con valor > 0, el backend lo deriva como la suma y ese valor manda.
     */
    estimatedValueCents: z.number().int().min(0).nullable().optional(),
    /** Líneas de servicio adicionales (la principal sigue siendo serviceLine). */
    additionalServiceLines: z.array(ServiceLineSchema).optional().default([]),
    /**
     * Mapa serviceLine → céntimos. Si trae keys con valor > 0, el backend deriva
     * serviceLine (la de mayor valor), additionalServiceLines (las otras con valor)
     * y estimatedValueCents (suma). Default {} (back compat: respeta los campos
     * sueltos del body).
     */
    serviceLineValuesCents: ServiceLineValuesSchema.optional().default({}),
    /**
     * LOTE B — Detalle por línea (fuente de verdad). Si se envía, el backend
     * escribe las filas OpportunityServiceLine y deriva serviceLine, adicionales,
     * estimatedValueCents, billingMode y las sumas facturable/no facturable. Los
     * campos sueltos y serviceLineValuesCents quedan como compatibilidad.
     */
    serviceLines: z.array(OpportunityServiceLineInputSchema).optional(),
});
export const UpdateOpportunitySchema = z.object({
    title: z.string().min(2).max(300).optional(),
    /**
     * Reasignar la oportunidad a otro cliente (p. ej. tras duplicar). El backend
     * lo rechaza (409) si ya hay facturación vinculada al cliente actual.
     */
    clientId: z.string().optional(),
    serviceLine: ServiceLineSchema.optional(),
    stage: PipelineStageSchema.optional(),
    billingMode: OpportunityBillingModeSchema.optional(),
    description: optionalString(5000),
    lostReason: optionalString(2000),
    /**
     * Cierre ADMINISTRATIVO: se marca PERDIDA solo para sacarla del pipeline
     * (duplicada, refacturada a otro cliente, alta errónea…), pero no es
     * negocio perdido y no cuenta en el KPI de perdidas ni en la conversión.
     */
    administrativeClose: z.boolean().optional(),
    openedAt: z.string().datetime().optional(),
    closedAt: z.string().datetime().nullable().optional(),
    advisorId: z.string().nullable().optional(),
    /** Comisión del asesor como fracción (0.05 = 5%). Tope de negocio: 0.30 (30%). */
    advisorCommissionPct: z.number().min(0).max(0.3).nullable().optional(),
    /** Fase de ejecución (solo proyectos GANADA). null la retira. */
    executionPhase: ExecutionPhaseSchema.nullable().optional(),
    /** Fecha de entrega del proyecto (se sella sola al marcar FINALIZADO). */
    deliveredAt: z.string().datetime().nullable().optional(),
    /**
     * Valor estimado manual del proyecto (en céntimos). Si serviceLineValuesCents
     * tiene keys con valor > 0, el backend lo deriva como la suma y ese valor manda.
     */
    estimatedValueCents: z.number().int().min(0).nullable().optional(),
    /** Líneas de servicio adicionales (la principal sigue siendo serviceLine). */
    additionalServiceLines: z.array(ServiceLineSchema).optional().default([]),
    /**
     * Mapa serviceLine → céntimos. Si se envía con keys con valor > 0, el backend
     * deriva serviceLine, additionalServiceLines y estimatedValueCents a partir
     * de él. Si se omite, no se toca; si se envía vacío {}, se respetan los campos
     * sueltos del body (back compat).
     */
    serviceLineValuesCents: ServiceLineValuesSchema.optional(),
    /**
     * LOTE B — Detalle por línea (fuente de verdad). Si se envía, reemplaza el
     * conjunto de líneas de la oportunidad y el backend re-deriva todo. Si se
     * omite, no se toca.
     */
    serviceLines: z.array(OpportunityServiceLineInputSchema).optional(),
});
export const OpportunityAssignmentSchema = z.object({
    id: z.string(),
    userId: z.string(),
    userFullName: z.string(),
    projectRoleId: z.string(),
    projectRoleName: z.string(),
    countsAsCAC: z.boolean(),
    hoursAllocated: z.number().int().min(0),
    hourlyRateCentsSnapshot: z.number().int().min(0),
    costPerHourCentsSnapshot: z.number().int().min(0),
    assignedAt: z.string().datetime(),
});
export const OpportunityListItemSchema = z.object({
    id: z.string(),
    code: z.string(),
    title: z.string(),
    stage: PipelineStageSchema,
    serviceLine: ServiceLineSchema,
    billingMode: OpportunityBillingModeSchema,
    clientId: z.string(),
    clientDisplayName: z.string(),
    openedAt: z.string().datetime(),
    closedAt: z.string().datetime().nullable(),
    assignedCount: z.number().int(),
    documentsCount: z.number().int(),
    /** Valor estimado manual del proyecto (en céntimos); null si no se ha fijado. */
    estimatedValueCents: z.number().int().nullable(),
    /** Parte FACTURABLE del valor (suma de líneas facturables); null si ninguna. */
    billableValueCents: z.number().int().nullable().optional(),
    /** Parte de INVERSIÓN (probono/pro-equity, no facturable). 0 si ninguna. */
    nonBillableValueCents: z.number().int().default(0),
});
/**
 * Rentabilidad de la oportunidad. Se devuelven dos vistas paralelas:
 *  - PRESUPUESTADO (`budgeted…`): de las asignaciones — `hoursAllocated *
 *    costSnapshot`, separadas por `ProjectRole.countsAsCAC`. Para forecast
 *    pre-venta y comparativa contra el real.
 *  - REAL (`acquisition…` / `execution…`): de los TimeEntries — horas
 *    registradas × costRateCents, separadas por `TimeEntryPhase`.
 * `capCents` y `grossMarginCents` se calculan con el REAL (lo único que se
 * factura/se cobra).
 * Importes en céntimos; tiempos en minutos (real) u horas (presupuestado).
 */
export const OpportunityProfitabilitySchema = z.object({
    // Presupuestado (asignaciones)
    budgetedCacHours: z.number().int(),
    budgetedCacCostCents: z.number().int(),
    budgetedExecHours: z.number().int(),
    budgetedExecCostCents: z.number().int(),
    // Real (TimeEntries)
    acquisitionHoursMinutes: z.number().int(),
    acquisitionCostCents: z.number().int(),
    /** Gastos de actividad (dietas, transporte…) imputados a PREVENTA — entran en el CAP. */
    acquisitionExpensesCents: z.number().int().default(0),
    /** Ingreso NETO (base imponible sin IVA), en céntimos. */
    revenueCents: z.number().int(),
    advisorCommissionCents: z.number().int(),
    capCents: z.number().int(),
    executionHoursMinutes: z.number().int(),
    executionCostCents: z.number().int(),
    /** Gastos de actividad imputados a EJECUCION — restan del margen bruto. */
    executionExpensesCents: z.number().int().default(0),
    grossMarginCents: z.number().int(),
    /** Margen bruto como fracción (0.42 = 42%); puede ser negativo. */
    grossMarginPct: z.number(),
});
export const OpportunityDetailSchema = OpportunityListItemSchema.extend({
    description: z.string().nullable(),
    lostReason: z.string().nullable(),
    administrativeClose: z.boolean().default(false),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    assignments: z.array(OpportunityAssignmentSchema),
    canManage: z.boolean(),
    canWrite: z.boolean(),
    advisorId: z.string().nullable(),
    advisorName: z.string().nullable(),
    /** Comisión del asesor como fracción (0.05 = 5%). */
    advisorCommissionPct: z.number().nullable(),
    /** Fase de ejecución (null mientras está en pipeline comercial). */
    executionPhase: ExecutionPhaseSchema.nullable(),
    /** Fecha de entrega del proyecto (null hasta marcar FINALIZADO). */
    deliveredAt: z.string().datetime().nullable(),
    /** Valor estimado manual del proyecto (en céntimos); null si no se ha fijado. */
    estimatedValueCents: z.number().int().nullable(),
    /** Líneas de servicio adicionales (la principal sigue siendo serviceLine). */
    additionalServiceLines: z.array(ServiceLineSchema),
    /**
     * Mapa serviceLine → céntimos con el valor por línea de servicio marcada.
     * {} si la oportunidad solo usa serviceLine + additionalServiceLines sin
     * importes por línea (oportunidades anteriores a esta funcionalidad).
     */
    serviceLineValuesCents: ServiceLineValuesSchema,
    /**
     * LOTE B — Detalle por línea de servicio de la oportunidad (valor, modo de
     * facturación y recurrencia). Fuente de verdad de la UI. Si la oportunidad es
     * anterior a esta funcionalidad, se sintetiza de serviceLine + adicionales.
     */
    serviceLines: z.array(OpportunityServiceLineSchema),
    profitability: OpportunityProfitabilitySchema,
});
/** Columnas ordenables del listado de oportunidades (vista tabla). */
export const OpportunitySortBySchema = z.enum(['code', 'title', 'client', 'value']);
export const OpportunityListQuerySchema = z.object({
    q: z.string().max(200).optional(),
    stage: PipelineStageSchema.optional(),
    serviceLine: ServiceLineSchema.optional(),
    clientId: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    // Techo 500 (no 100) para que los selectores de oportunidad no trunquen en
    // silencio. Default en 25 para los listados paginados normales.
    pageSize: z.coerce.number().int().min(1).max(500).default(25),
    /** Sin sortBy: relevancia si hay búsqueda, si no apertura DESC. */
    sortBy: OpportunitySortBySchema.optional(),
    sortDir: z.enum(['asc', 'desc']).default('asc'),
});
export const OpportunityListResponseSchema = z.object({
    items: z.array(OpportunityListItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
// Stats agregadas del pipeline para el kanban y los KPIs superiores.
// Importes en céntimos. valueCents = suma de Quote.subtotalCents del más reciente
// por oportunidad (estimación de valor); weightedCents = valueCents × probabilidad
// de la etapa (LEAD 5%, … PROPUESTA_ENVIADA 60%, GANADA 100%, PERDIDA 0%).
export const OpportunityStageStatsSchema = z.object({
    stage: PipelineStageSchema,
    count: z.number().int(),
    valueCents: z.number().int(),
    weightedCents: z.number().int(),
    /**
     * Valor INVERTIDO (probono/pro-equity, no facturable) de las oportunidades de
     * esta etapa. Se usa para mostrar "Invertido: X" en el pie de las columnas
     * EN INVERSIÓN y GANADA. Default 0 (back compat).
     */
    investedCents: z.number().int().default(0),
});
export const OpportunityStatsResponseSchema = z.object({
    byStage: z.array(OpportunityStageStatsSchema),
    /** Pipeline COMPLETO (todas las etapas activas + GANADA + PERDIDA). */
    total: z.object({
        count: z.number().int(),
        valueCents: z.number().int(),
        weightedCents: z.number().int(),
    }),
    /** Solo etapas activas (excluye GANADA y PERDIDA). */
    open: z.object({
        count: z.number().int(),
        valueCents: z.number().int(),
        weightedCents: z.number().int(),
    }),
    /** Solo GANADA. */
    won: z.object({
        count: z.number().int(),
        valueCents: z.number().int(),
    }),
    /**
     * INVERTIDO — Probono/Pro-Equity REALIZADO: solo suma la parte no facturable
     * de las oportunidades en EN INVERSIÓN o GANADA (inversión ya comprometida, no
     * hipotética en fases previas del embudo). byStage/total/open/won cuentan solo
     * la parte FACTURABLE.
     */
    nonBillable: z.object({
        count: z.number().int(),
        valueCents: z.number().int(),
    }),
});
export const AssignUserSchema = z.object({
    userId: z.string().min(1),
    projectRoleId: z.string().min(1),
    /** Horas presupuestadas para esta asignación. 0 = no se ha estimado todavía. */
    hoursAllocated: z.number().int().min(0).max(10_000).default(0),
    /**
     * Override opcional de tarifa/coste. Si null/undefined se toma del
     * UserProjectRole del usuario. Si el UserProjectRole no existe → 400.
     */
    hourlyRateCentsOverride: z.number().int().min(0).max(1_000_000_00).nullable().optional(),
    costPerHourCentsOverride: z.number().int().min(0).max(1_000_000_00).nullable().optional(),
});
export const OpportunityEventTypeSchema = z.enum([
    'OPPORTUNITY_CREATED',
    'STAGE_CHANGED',
    'ASSIGNMENT_ADDED',
    'ASSIGNMENT_REMOVED',
    'DOCUMENT_ADDED',
    'DOCUMENT_VERSION_ADDED',
    'NOTE',
    'CONTACT_LOG',
    'EMAIL_ARCHIVED',
    'CALENDAR_EVENT_ADDED',
    'CALENDAR_EVENT_REMOVED',
    'QUOTE_SENT',
    'QUOTE_ACCEPTED',
    'EXECUTION_PHASE_CHANGED',
]);
export const OpportunityEventTypeLabel = {
    OPPORTUNITY_CREATED: 'Oportunidad creada',
    STAGE_CHANGED: 'Cambio de etapa',
    ASSIGNMENT_ADDED: 'Asignación añadida',
    ASSIGNMENT_REMOVED: 'Asignación retirada',
    DOCUMENT_ADDED: 'Documento añadido',
    DOCUMENT_VERSION_ADDED: 'Nueva versión',
    NOTE: 'Nota',
    CONTACT_LOG: 'Contacto registrado',
    EMAIL_ARCHIVED: 'Email archivado',
    CALENDAR_EVENT_ADDED: 'Evento de agenda añadido',
    CALENDAR_EVENT_REMOVED: 'Evento de agenda retirado',
    QUOTE_SENT: 'Propuesta enviada',
    QUOTE_ACCEPTED: 'Propuesta aceptada',
    EXECUTION_PHASE_CHANGED: 'Cambio de fase de ejecución',
};
export const OpportunityEventSchema = z.object({
    id: z.string(),
    type: OpportunityEventTypeSchema,
    title: z.string(),
    description: z.string().nullable(),
    occurredAt: z.string().datetime(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    documentId: z.string().nullable(),
});
export const OpportunityEventListResponseSchema = z.object({
    items: z.array(OpportunityEventSchema),
});
export const CreateOpportunityEventSchema = z.object({
    title: z.string().min(1).max(300),
    description: z.string().max(5000).optional().nullable(),
    type: z.enum(['NOTE', 'CONTACT_LOG']).default('NOTE'),
    occurredAt: z.string().datetime().optional(),
});
// ===== Pieza 5 — Roadmap de ejecución =====
export const RoadmapItemSchema = z.object({
    id: z.string(),
    position: z.number().int(),
    title: z.string(),
    description: z.string().nullable(),
    phase: ExecutionPhaseSchema.nullable(),
    status: RoadmapItemStatusSchema,
    plannedStart: z.string().datetime().nullable(),
    plannedEnd: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const RoadmapListResponseSchema = z.object({
    items: z.array(RoadmapItemSchema),
});
export const CreateRoadmapItemSchema = z.object({
    title: z.string().min(1, 'Título requerido').max(300),
    description: z.string().max(2000).nullable().optional(),
    phase: ExecutionPhaseSchema.nullable().optional(),
    status: RoadmapItemStatusSchema.default('PENDIENTE'),
    position: z.number().int().min(0).optional(),
    plannedStart: z.string().datetime().nullable().optional(),
    plannedEnd: z.string().datetime().nullable().optional(),
});
export const UpdateRoadmapItemSchema = z.object({
    title: z.string().min(1).max(300).optional(),
    description: z.string().max(2000).nullable().optional(),
    phase: ExecutionPhaseSchema.nullable().optional(),
    status: RoadmapItemStatusSchema.optional(),
    position: z.number().int().min(0).optional(),
    plannedStart: z.string().datetime().nullable().optional(),
    plannedEnd: z.string().datetime().nullable().optional(),
});
//# sourceMappingURL=opportunities.js.map